import { useMemo } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "../../utilities/helpers";
import { useCurrency } from "../dropdown";
import {
	formatCompactCurrency,
	formatCurrency,
	formatSignedCurrency,
	formatSignedPercent,
	useInvestmentSummaryQuery,
} from "./shared";
import { usePortfolioReturns } from "./useReturns";

const WHOLE = { maximumFractionDigits: 0 };

// Placeholder bars, not zeros: a currency switch refetches under a new key, and
// "$0.00 total return 0.00%" is a claim, not a blank.
const Placeholder = ({ className }) => (
	<span
		aria-hidden="true"
		className={cn("block animate-pulse rounded bg-slate-100", className)}
	/>
);

const signTone = (value) =>
	value > 0 ? "text-emerald-700" : value < 0 ? "text-rose-700" : "text-slate-900";

// A high-magnitude currency (INR, JPY) pushes these figures past the width of a
// tile, and the browser takes the break opportunity right after the sign --
// leaving a lone "+" sitting above its own number. Keeping the value unbreakable
// and stepping the size down past a length that no longer fits is what holds the
// row together across every currency.
const Stat = ({ label, value, hint, tone, title, loading }) => (
	<div className="min-w-0">
		<dt className="text-xs font-medium text-slate-500" title={title}>
			{label}
		</dt>
		{loading ? (
			<dd className="mt-1">
				<Placeholder className="h-6 w-20" />
			</dd>
		) : (
			<>
				<dd
					className={cn(
						"mt-1 whitespace-nowrap font-semibold text-slate-900",
						String(value).length > 10 ? "text-sm" : "text-lg",
						tone
					)}
				>
					{value}
				</dd>
				{hint && <dd className="mt-0.5 text-xs text-slate-500">{hint}</dd>}
			</>
		)}
	</div>
);

const Summary = () => {
	const { currency } = useCurrency();
	const { data: summary, isLoading } = useInvestmentSummaryQuery();
	const returns = usePortfolioReturns();
	const ready = Boolean(summary);

	const metrics = useMemo(() => {
		const totals = summary?.totals || {};
		const totalReturn =
			(totals.realizedPL || 0) + (totals.unrealizedPL || 0) + (totals.dividendIncome || 0);
		// Same denominator as the chart's "net invested" line: buys minus sale proceeds.
		const netInvested = (totals.boughtCost || 0) - (totals.soldProceeds || 0);
		const percent = netInvested > 0 ? (totalReturn / netInvested) * 100 : 0;

		return {
			worth: totals.currentWorth || 0,
			totalReturn,
			percent,
			netInvested,
			costBasis: totals.remainingCostBasis || 0,
			unrealized: totals.unrealizedPL || 0,
			realized: totals.realizedPL || 0,
			dividends: totals.dividendIncome || 0,
		};
	}, [summary]);

	const TrendIcon = metrics.totalReturn >= 0 ? TrendingUp : TrendingDown;
	const moneyWeightedPercent = returns.moneyWeighted.percent;
	const timeWeighted = returns.timeWeighted;

	return (
		<section
			aria-label="Portfolio summary"
			className={cn(
				"rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-opacity md:p-6",
				isLoading && "opacity-60"
			)}
		>
			<div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
				<div className="shrink-0">
					<p className="text-sm font-medium text-slate-500">Portfolio value</p>
					{!ready ? (
						<>
							<Placeholder className="mt-2 h-10 w-64 md:h-12" />
							<Placeholder className="mt-4 h-5 w-52" />
						</>
					) : (
					<>
					<p className="mt-1 text-4xl font-semibold tracking-tight text-slate-900 md:text-5xl">
						{formatCurrency(metrics.worth, currency)}
					</p>
					<p className="mt-3 flex flex-wrap items-center gap-2 text-sm">
						<span
							className={cn(
								"inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-medium",
								metrics.totalReturn >= 0
									? "bg-emerald-50 text-emerald-700"
									: "bg-rose-50 text-rose-700"
							)}
						>
							<TrendIcon className="h-4 w-4" aria-hidden="true" />
							{formatSignedCurrency(metrics.totalReturn, currency)}
							<span className="font-normal">({formatSignedPercent(metrics.percent)})</span>
						</span>
						<span className="text-slate-500">total return, all time</span>
					</p>
					</>
					)}
				</div>

				<dl className="grid grid-cols-2 gap-x-6 gap-y-5 border-t border-slate-100 pt-5 sm:grid-cols-3 lg:grid-cols-6 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
					<Stat
						loading={!ready}
						label="Net invested"
						value={formatCurrency(metrics.netInvested, currency, WHOLE)}
						hint="Buys minus sale proceeds"
					/>
					<Stat
						loading={!ready}
						label="Unrealized P/L"
						value={formatSignedCurrency(Math.round(metrics.unrealized), currency).replace(/\.00$/, "")}
						hint={`On ${formatCompactCurrency(metrics.costBasis, currency)} cost basis`}
						tone={signTone(metrics.unrealized)}
					/>
					<Stat
						loading={!ready}
						label="Realized P/L"
						value={formatSignedCurrency(Math.round(metrics.realized), currency).replace(/\.00$/, "")}
						hint="From completed sales"
						tone={signTone(metrics.realized)}
					/>
					<Stat
						loading={!ready}
						label="Dividends"
						value={formatCurrency(metrics.dividends, currency, WHOLE)}
						hint="Received, all time"
					/>
					<Stat
						loading={!ready}
						label="Money-weighted"
						value={
							moneyWeightedPercent === null
								? "—"
								: formatSignedPercent(moneyWeightedPercent, 1)
						}
						hint="Return on your money, per year"
						tone={signTone(moneyWeightedPercent || 0)}
						title={`Annualized internal rate of return (XIRR) across every buy, sale and dividend, so capital added late is only credited for the time it was invested. The simple CAGR from the first trade reads ${formatSignedPercent(returns.cagrPercent, 1)}, which spreads the return over a window most of the money was not in.`}
					/>
					<Stat
						loading={!ready}
						label="Time-weighted"
						value={
							timeWeighted && timeWeighted.stepCount
								? formatSignedPercent(timeWeighted.annualizedPercent, 1)
								: "—"
						}
						hint={
							timeWeighted?.isAnnualized === false
								? "Since inception, too short to annualize"
								: "The picks, timing removed"
						}
						tone={signTone(timeWeighted?.annualizedPercent || 0)}
						title="Daily returns chained together, so the size and timing of contributions cannot flatter or penalise the figure. This is the rate to compare against an index; money-weighted is what your own money earned."
					/>
				</dl>
			</div>
		</section>
	);
};

export default Summary;
